import { useState } from 'react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import { useTransferActionMutation, useTransferQuery } from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDateTime, formatMoney, formatQuantity } from '@/lib/format';
import {
  Alert, Badge, Button, ErrorState, Input, LoadingState, Modal, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { TRANSFER_TONE } from './TransfersPage';

/**
 * One transfer, with the actions its current status allows.
 *
 * The buttons mirror the API's state machine exactly: anything the server would
 * reject is not offered here, so a user never learns a step is impossible by
 * being told off for trying it.
 */
export function TransferDetailModal({
  transferId,
  onClose,
}: {
  transferId: string;
  onClose: () => void;
}) {
  const format = useFormat();
  const { data: transfer, isLoading, isError, error, refetch } = useTransferQuery(transferId);
  const [runAction, actionState] = useTransferActionMutation();

  const [actionError, setActionError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  /** Received quantities, keyed by line id — only used on the receive step. */
  const [received, setReceived] = useState<Record<string, string>>({});

  const act = async (action: string, body?: Record<string, unknown>): Promise<void> => {
    setActionError(null);
    try {
      await runAction({ id: transferId, action, body }).unwrap();
      setRejecting(false);
      setRejectReason('');
    } catch (failure) {
      setActionError(apiErrorMessage(failure, `The transfer could not be ${action}ed`));
    }
  };

  const receive = async (): Promise<void> => {
    const lines = Object.entries(received)
      .filter(([, value]) => value !== '')
      .map(([itemId, value]) => ({ itemId, receivedQty: Number(value) }));

    // No edits means everything dispatched arrived.
    await act('receive', lines.length > 0 ? { lines } : undefined);
  };

  const status = transfer?.status;

  return (
    <Modal
      open
      onClose={onClose}
      title={transfer ? `Transfer ${transfer.number}` : 'Transfer'}
      description={
        transfer
          ? `${transfer.sourceWarehouse.name} → ${transfer.destinationWarehouse.name}`
          : undefined
      }
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Close</Button>

          {status === 'DRAFT' && (
            <Can permission={P.STOCK_TRANSFER}>
              <Button loading={actionState.isLoading} onClick={() => void act('submit')}>
                Submit for approval
              </Button>
            </Can>
          )}

          {status === 'REQUESTED' && (
            <Can permission={P.STOCK_TRANSFER_APPROVE}>
              <Button variant="outline" onClick={() => setRejecting(true)}>Reject</Button>
              <Button loading={actionState.isLoading} onClick={() => void act('approve')}>
                Approve
              </Button>
            </Can>
          )}

          {status === 'APPROVED' && (
            <Can permission={P.STOCK_TRANSFER_APPROVE}>
              <Button loading={actionState.isLoading} onClick={() => void act('dispatch')}>
                Dispatch
              </Button>
            </Can>
          )}

          {status === 'DISPATCHED' && (
            <Can permission={P.STOCK_TRANSFER_APPROVE}>
              <Button loading={actionState.isLoading} onClick={() => void receive()}>
                Confirm receipt
              </Button>
            </Can>
          )}
        </>
      }
    >
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
      ) : transfer ? (
        <div className="space-y-4">
          {actionError && <Alert tone="danger">{actionError}</Alert>}

          <div className="flex flex-wrap items-center gap-3">
            <Badge tone={TRANSFER_TONE[transfer.status]}>
              {transfer.status === 'DISPATCHED' ? 'in transit' : transfer.status.toLowerCase()}
            </Badge>
            {transfer.dispatchedAt && (
              <span className="text-xs text-muted">
                Dispatched {formatDateTime(transfer.dispatchedAt, format)}
              </span>
            )}
            {transfer.receivedAt && (
              <span className="text-xs text-muted">
                Received {formatDateTime(transfer.receivedAt, format)}
              </span>
            )}
          </div>

          {status === 'DISPATCHED' && (
            <Alert tone="info">
              These goods have left {transfer.sourceWarehouse.name} and are counted in neither
              warehouse until receipt. Any shortfall you record is written off as a loss.
            </Alert>
          )}

          {transfer.rejectReason && <Alert tone="warning">{transfer.rejectReason}</Alert>}

          {rejecting && (
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Input
                  label="Reason for rejection"
                  value={rejectReason}
                  onChange={(event) => setRejectReason(event.target.value)}
                />
              </div>
              <Button
                variant="danger"
                disabled={!rejectReason}
                loading={actionState.isLoading}
                onClick={() => void act('reject', { reason: rejectReason })}
              >
                Confirm rejection
              </Button>
              <Button variant="ghost" onClick={() => setRejecting(false)}>Cancel</Button>
            </div>
          )}

          <Table>
            <thead>
              <tr>
                <Th>Product</Th>
                <Th numeric>Requested</Th>
                <Th numeric>Dispatched</Th>
                <Th numeric>{status === 'DISPATCHED' ? 'Receiving' : 'Received'}</Th>
                <Th numeric>Variance</Th>
                <Th numeric>Unit cost</Th>
              </tr>
            </thead>
            <tbody>
              {transfer.items?.map((item) => (
                <tr key={item.id}>
                  <Td>
                    <p>{item.variant.product.name}</p>
                    <p className="text-xs text-muted">{item.variant.sku}</p>
                  </Td>
                  <Td numeric className="text-secondary">
                    {formatQuantity(item.requestedQty, format)}
                  </Td>
                  <Td numeric className="text-secondary">
                    {formatQuantity(item.dispatchedQty, format)}
                  </Td>
                  <Td numeric>
                    {status === 'DISPATCHED' ? (
                      <Can
                        permission={P.STOCK_TRANSFER_APPROVE}
                        fallback={<span>{formatQuantity(item.receivedQty, format)}</span>}
                      >
                        <Input
                          type="number"
                          min="0"
                          step="0.0001"
                          className="h-9 w-24 text-right"
                          placeholder={item.dispatchedQty}
                          value={received[item.id] ?? ''}
                          onChange={(event) =>
                            setReceived({ ...received, [item.id]: event.target.value })
                          }
                        />
                      </Can>
                    ) : (
                      formatQuantity(item.receivedQty, format)
                    )}
                  </Td>
                  <Td numeric className={item.varianceQty !== '0' ? 'text-red-600' : 'text-secondary'}>
                    {formatQuantity(item.varianceQty, format)}
                  </Td>
                  <Td numeric className="text-secondary">
                    {formatMoney(item.unitCost, format)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>

          {transfer.notes && <p className="text-sm text-secondary">{transfer.notes}</p>}
        </div>
      ) : null}
    </Modal>
  );
}
